import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAccessStations, useFlightAccess } from '@/app/flightAccess';
import { FlightAccessNote } from '@/components/FlightAccessNote';
import { Button } from '@/components/ui/Button';
import { Named } from '@/components/ui/Named';
import { InfoIcon } from '@/components/ui/icons';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { SortArrow } from '@/components/ui/SortArrow';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { useAirlines } from '@/lib/api/airlines';
import { useAirportCities, useStationOptions } from '@/lib/api/airports';
import { dayMonth, dayOffset, formatDrift, minutesBetween } from '@/lib/clock';
import { cx } from '@/lib/cx';
import { saveBlob } from '@/lib/download';
import { cityOf } from '@/types/airport';
import type { AirportCity } from '@/types/airport';
import { defaultStation, isRestricted } from '@/types/flightAccess';
import type { FlightRow, SortableField } from '@/types/flight';
import { FLIGHT_STATUSES, FLIGHT_TYPES } from '@/types/flight';
import { downloadScheduleApcas, downloadScheduleExcel } from './api';
import { AirlineFilter } from './AirlineFilter';
import { ExportMenu } from './ExportMenu';
import type { ExportKind } from './ExportMenu';
import { AirportFilter } from './AirportFilter';
import { TextFilter } from './ColumnFilter';
import { FlightPanel } from './FlightPanel';
import { RangePicker } from './RangePicker';
import { StatusFilter } from './StatusFilter';
import { TINT } from './statusTint';
import { coversOnlyToday, ROWS_OPTIONS, useScheduleParams } from './useScheduleParams';
import type { ScheduleParams } from './useScheduleParams';
import { useSchedule } from './useSchedule';
import styles from './SchedulePage.module.css';

/**
 * The online table.
 *
 * Filters, page and sort all live in the URL, so a refresh keeps them, a link
 * carries them and Back works — see useScheduleParams for why that is the state
 * rather than a copy of it.
 *
 * Eight columns, chosen rather than everything the endpoint returns: a board is read
 * at a glance, and a column nobody looks at costs the ones they do. The station and
 * the leg sit ABOVE the table, because they say which board this is rather than
 * which of its rows — and they are exactly what the API doc says a board filters on.
 */
export const SchedulePage = () => {
  /*
    A reader whose access names airports picks a station from those airports only, and
    one with a single airport is on that airport's board — derived in the params rather
    than written into the URL, so a link, Back and "Clear all" all agree with it.
  */
  const stationsOnly = useAccessStations();
  const { params, criteria, update, reset, hasFilters } = useScheduleParams(stationsOnly);
  const { rows, currPage, total, isLastPage, isLoading, errorKey } = useSchedule(criteria);
  const stationOptions = useStationOptions(stationsOnly);
  const { t, locale } = useTranslation();
  const { access } = useFlightAccess();

  /*
    The rows are already inside the reader's flight access — the server answers inside
    it on every request, and nothing here filters. This only decides what to SAY: an
    empty board is "nothing inside your access" for a restricted reader, not a fault.
  */
  const restricted = isRestricted(access);

  const setSort = useCallback(
    (field: SortableField) => {
      const sameField = params.sort === field;
      update({
        sort: field,
        dir: sameField && params.dir === 'ascending' ? 'descending' : 'ascending',
      });
    },
    [params.sort, params.dir, update],
  );

  /**
   * Does a time on this board need a date beside it?
   *
   * Yes, unless the board is today — that is the only period a reader can date from
   * memory. Decided once here and handed to every row, rather than asked fifty times
   * for the same answer.
   *
   * It used to ask whether the period crossed midnight, which left a single past day
   * undated: choosing Yesterday gave a page of bare times that read as this
   * afternoon's.
   */
  const datedTimes = !coversOnlyToday({ dateFrom: params.dateFrom, dateTo: params.dateTo });

  /**
   * The cities behind the codes on this page.
   *
   * Only the codes actually shown: a board is fifty rows of at most a hundred codes,
   * usually far fewer, because one end of every row is the station being viewed.
   */
  const shownCodes = useMemo(
    () => rows.flatMap((row) => [row.departureAirportIataCode, row.arrivalAirportIataCode]),
    [rows],
  );
  const airportOf = useAirportCities(shownCodes);

  /**
   * Carrier names by IATA code.
   *
   * Free: `useAirlines` is already fetched and cached for the airline filter on this
   * same screen, so naming the column costs no request.
   *
   * It is the WORKING LIST, not the whole dictionary — `/airline/all` filters on
   * `kzFlying`, see lib/api/airlines.ts. A carrier off that list shows as its bare
   * code, which is the same set the filter above can offer: a code with no name here
   * is a code you also cannot pick up there, rather than two different ideas of which
   * airlines exist.
   */
  const { airlines } = useAirlines();

  const airlineOf = useMemo(() => {
    const byCode = new Map<string, string>();

    for (const airline of airlines) {
      const code = (airline.iataCode ?? '').trim().toUpperCase();
      const title = (airline.title ?? '').trim();
      if (code !== '' && title !== '') byCode.set(code, title);
    }

    return (code: string | null) => byCode.get((code ?? '').trim().toUpperCase()) ?? '';
  }, [airlines]);

  /**
   * The exports, driven by the SAME criteria the table shows. What the reader has
   * filtered is what the file holds — including the period, which matters: the
   * endpoints have no row cap, and criteria always carry a scheduledDate range
   * because the board always has one.
   *
   * One at a time: the menu's trigger is disabled while one runs, because two
   * exports of the whole board at once is twice the server work for a click that
   * was almost certainly a double-click.
   */
  const [exporting, setExporting] = useState<ExportKind | null>(null);
  const [exportFailed, setExportFailed] = useState(false);

  const exportFile = useCallback(
    async (kind: ExportKind) => {
      setExporting(kind);
      setExportFailed(false);

      try {
        if (kind === 'excel') {
          // The server names the file itself, but that name is in a header fetch
          // hands over inconsistently — so it is stated here, matching the doc.
          saveBlob(await downloadScheduleExcel(criteria, locale), 'efreight_schedule.xlsx');
        } else {
          saveBlob(await downloadScheduleApcas(criteria), 'apcas_schedule.csv');
        }
      } catch {
        // One line either way: a 500 from APCAS generation arrives with an empty
        // body, so there is rarely a server message worth relaying.
        setExportFailed(true);
      } finally {
        setExporting(null);
      }
    },
    [criteria, locale],
  );

  return (
    <div className={cx(styles.page, params.id !== null && styles.split)}>
      <div className={styles.main}>
      <div className={styles.toolbar}>
        <h1 className={styles.title}>{t('schedule.title')}</h1>

        {/* Which flights this reader sees, for a restricted reader only. The other
            filters stay as they are: an Astana reader may still pick Almaty, and gets
            the Almaty departures of flights to Astana. */}
        <FlightAccessNote />

        <div className={styles.spacer} />

        {/* The exports, beside the filters they obey. */}
        <ExportMenu busy={exporting} onPick={exportFile} />

        <Button size="sm" onClick={reset} disabled={!hasFilters}>
          {t('schedule.resetFilters')}
        </Button>
      </div>

      {exportFailed && (
        <p className={styles.error} role="alert">
          {t('schedule.export.error')}
        </p>
      )}

      {/* Which board, as opposed to which rows of it. */}
      <div className={styles.boardFilters}>
        <label className={styles.boardFilter}>
          <span className={styles.boardFilterLabel}>{t('schedule.columns.station')}</span>
          {/* The reader's own airports when their access names any. "All" then means
              all of THEIRS, and is not offered at all for a single airport — that one
              airport is the board. Marked active only when it differs from where the
              board opens, which is what a filter is. */}
          <Select
            options={stationOptions}
            {...(stationsOnly === null
              ? { placeholder: t('schedule.allStations') }
              : stationsOnly.length > 1
                ? { placeholder: t('flightAccess.allYourStations') }
                : {})}
            value={params.airport}
            active={params.airport !== defaultStation(stationsOnly)}
            onChange={(event) => update({ airport: event.target.value })}
            aria-label={t('schedule.columns.station')}
          />
        </label>

        <label className={styles.boardFilter}>
          <span className={styles.boardFilterLabel}>{t('schedule.columns.leg')}</span>
          <Select
            options={FLIGHT_TYPES.map((type) => ({
              value: type,
              label: t(`schedule.types.${type}`),
            }))}
            placeholder={t('schedule.allTypes')}
            value={params.type}
            active={params.type !== ''}
            onChange={(event) => update({ type: event.target.value as ScheduleParams['type'] })}
            aria-label={t('schedule.columns.leg')}
          />
        </label>

        {/* At the end of the row, because it says WHEN this board is rather than
            which of its rows — the same kind of statement as the station, and the one
            a reader changes most often. There is always a period: the API has no row
            cap, so "no date filter" means the whole table. */}
        <div className={cx(styles.boardFilter, styles.boardFilterEnd)}>
          <span className={styles.boardFilterLabel}>{t('schedule.range.label')}</span>

          <div className={styles.period}>
            <RangePicker
              preset={params.range}
              range={{ dateFrom: params.dateFrom, dateTo: params.dateTo }}
              onPickPreset={(range) => update({ range, dateFrom: '', dateTo: '' })}
              onPickRange={(range) => update({ ...range, range: null })}
            />

          </div>
        </div>
      </div>

      {errorKey && (
        <p className={styles.error} role="alert">
          {t(errorKey)}
        </p>
      )}

      <div className={styles.tableWrap}>
        {isLoading && <div className={styles.loadingBar} aria-hidden="true" />}

        <table className={cx(styles.table, isLoading && styles.loading)}>
          <thead>
            <tr className={styles.headRow}>
              {COLUMNS.map((column) => (
                <SortHead
                  key={column.field}
                  field={column.field}
                  labelKey={column.labelKey}
                  sort={params.sort}
                  dir={params.dir}
                  onSort={setSort}
                  // The key to the row colours, beside the column those colours are
                  // about. Only here — a legend is one thing, not a header feature.
                  before={column.field === 'flightStatus' ? <StatusLegend /> : undefined}
                />
              ))}
            </tr>

            <tr className={styles.filterRow}>
              <th>
                {/* The one filter with a dictionary behind it: a code can be typed,
                    or found by the name of the airline that flies it. */}
                <AirlineFilter
                  value={params.airline}
                  onChange={(airline) => update({ airline })}
                  placeholder=""
                  ariaLabel={t('schedule.columns.airline')}
                />
              </th>
              <th>
                <TextFilter
                  value={params.flight}
                  onChange={(flight) => update({ flight })}
                  placeholder=""
                  ariaLabel={t('schedule.columns.flight')}
                  upper
                />
              </th>
              <th>
                {/* The stations to start with, then the whole world once two
                    characters are typed — see AirportFilter. */}
                <AirportFilter
                  values={params.from}
                  onChange={(from) => update({ from })}
                  placeholder=""
                  ariaLabel={t('schedule.columns.origin')}
                />
              </th>
              <th>
                <AirportFilter
                  values={params.to}
                  onChange={(to) => update({ to })}
                  placeholder=""
                  ariaLabel={t('schedule.columns.destination')}
                />
              </th>
              <th>
                <StatusFilter
                  values={params.status}
                  onChange={(status) => update({ status })}
                  ariaLabel={t('schedule.columns.status')}
                />
              </th>
              {/* No range here any more: the period picker above owns it, and two
                  controls for one fact meant the reader had to work out which of them
                  the board was obeying. */}
              <th />

              {/* Estimated and actual take no range: most rows on a live board have
                  neither yet, and a range on a null column drops them silently. */}
              <th />
              <th />

              {/*
                Nothing for the last three.

                The stand still filters — `planeParking` in the criteria, and the
                `stand` URL parameter still reaches it, so a saved link narrowing to
                one stand keeps working. It just has no box here: this is a board to
                read, and a stand is looked at rather than searched for.

                The other two could not have one. `delayReason` is not a criteria
                field at all, and `passengerAmount` filters by EQUALITY — a box that
                matches only flights carrying exactly 143 passengers is not a filter.
              */}
              <th />
              <th />
              <th />
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={COLUMNS.length} className={styles.message}>
                  {t('schedule.nothingFound')}
                  {/* Normal, not an error: a filter outside the access is simply empty. */}
                  {restricted && <span className={styles.messageHint}>{t('flightAccess.emptyHint')}</span>}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <ScheduleRow
                  key={row.id}
                  row={row}
                  datedTimes={datedTimes}
                  airline={airlineOf(row.airlineIataCode)}
                  from={airportOf(row.departureAirportIataCode)}
                  to={airportOf(row.arrivalAirportIataCode)}
                  isOpen={row.id === params.id}
                  onOpen={() => update({ id: row.id })}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        // The page the server RETURNED. Asking this API for a page past the end
        // answers with the last one, so showing the requested number would put page
        // 9's rows under a control that says 12. Nothing is written back to the URL
        // for it: the URL is the request, this is the result.
        page={currPage}
        rows={params.rows}
        total={total}
        countLabel={t('schedule.rowsFound', { count: total })}
        isLastPage={isLastPage}
        rowsOptions={ROWS_OPTIONS}
        onPage={(page) => update({ page })}
        onRows={(rowCount) => update({ rows: rowCount })}
        isLoading={isLoading}
      />

      {/* Both legs of a flight are separate rows, and a search by flight number
          returns both. Saying so once beats a reader thinking the table
          double-counts. */}
      {params.flight !== '' && rows.length > 1 && (
        <p className={styles.footnote}>{t('schedule.bothLegsHint')}</p>
      )}
      </div>

      {params.id !== null && (
        <FlightPanel
          id={params.id}
          rows={rows}
          onClose={() => update({ id: null })}
          onOpen={(id) => update({ id })}
        />
      )}
    </div>
  );
};

/**
 * The columns, in order.
 *
 * A list rather than eight repetitions of the same markup: the head row, the empty
 * filter cells and the "nothing found" colspan all have to agree on the count, and a
 * list is the only version of that which cannot drift.
 */
const COLUMNS: ReadonlyArray<{ field: SortableField; labelKey: Key }> = [
  { field: 'airlineIataCode', labelKey: 'schedule.columns.airline' },
  { field: 'flightNumber', labelKey: 'schedule.columns.flight' },
  { field: 'departureAirportIataCode', labelKey: 'schedule.columns.origin' },
  { field: 'arrivalAirportIataCode', labelKey: 'schedule.columns.destination' },
  { field: 'flightStatus', labelKey: 'schedule.columns.status' },
  { field: 'scheduledDate', labelKey: 'schedule.columns.scheduled' },
  { field: 'estimatedDate', labelKey: 'schedule.columns.estimated' },
  { field: 'actualDate', labelKey: 'schedule.columns.actual' },
  // The operational three, after the times rather than among them: scheduled,
  // estimated and actual are read as one block and a column between them breaks it.
  { field: 'planeParkingName', labelKey: 'schedule.columns.stand' },
  { field: 'delayReason', labelKey: 'schedule.columns.delayReason' },
  { field: 'passengerAmount', labelKey: 'schedule.columns.passengers' },
];

/**
 * A sortable column head.
 *
 * Defined at module level, not inside SchedulePage: a component created during a
 * render is a brand-new type each time, so React unmounts and remounts its whole
 * subtree — which in a table means the filter input below it loses focus mid-word.
 */
const SortHead = ({
  field,
  labelKey,
  sort,
  dir,
  onSort,
  before,
}: {
  field: SortableField;
  labelKey: Key;
  sort: SortableField;
  dir: 'ascending' | 'descending';
  onSort: (field: SortableField) => void;
  /** Rendered before the label — a sibling of the sort button, never inside it, so
      reaching for it cannot reorder the table. */
  before?: ReactNode;
}) => {
  const { t } = useTranslation();
  const sorted = sort === field;

  return (
    <th>
      <span className={styles.head}>
        {before}
        <button
          type="button"
          className={cx(styles.sortButton, sorted && styles.sorted)}
          onClick={() => onSort(field)}
          title={t('schedule.sortHint')}
        >
          {t(labelKey)}
          <SortArrow
            sorted={sorted}
            dir={dir}
            className={styles.sortArrow}
            idleClassName={styles.sortIdle}
          />
        </button>
      </span>
    </th>
  );
};

/** One leg. */
const ScheduleRow = ({
  row,
  datedTimes,
  airline,
  from,
  to,
  isOpen,
  onOpen,
}: {
  row: FlightRow;
  /** True when the board spans days, so the scheduled time needs a date beside it. */
  datedTimes: boolean;
  /** The carrier's short name, or '' when the working list does not name it. */
  airline: string;
  /** The two airports, when the dictionary knows them — see `useAirportCities`. */
  from: AirportCity | null;
  to: AirportCity | null;
  isOpen: boolean;
  onOpen: () => void;
}) => {
  const { t, locale, formatNumber } = useTranslation();

  const tint = row.flightStatus ? TINT[row.flightStatus] : undefined;

  const scheduled = row.scheduledDate;
  const estimated = row.estimatedDate;

  // An estimate that has moved off the schedule is the most important number on a
  // board, so it is marked rather than left to be spotted.
  const estimateMoved =
    scheduled !== null &&
    estimated !== null &&
    Math.abs(new Date(estimated).getTime() - new Date(scheduled).getTime()) >= 60_000;

  return (
    // The whole row opens the card — a row is one flight, and the reader aiming at a
    // six-character flight number to see the rest of it was a target for no reason.
    // The button stays: it is what a keyboard reaches and what a screen reader
    // announces, neither of which a click handler on a <tr> gives. Its own click
    // bubbles to this one and both call the same thing, which is idempotent.
    <tr
      className={cx(styles.row, tint, tint && styles.tinted, isOpen && styles.selected)}
      onClick={onOpen}
    >
      <td>
        <Named code={row.airlineIataCode} name={airline} showCode={false} />
      </td>
      <td>
        {/* A button, not a link: the panel opens beside the table rather than
            navigating away, because the next thing a reader does is open the row
            below this one. */}
        <button
          type="button"
          className={styles.flightButton}
          onClick={onOpen}
          aria-expanded={isOpen}
        >
          {row.flightNumber ?? '—'}
        </button>
      </td>
      <td>
        <Named
          code={row.departureAirportIataCode}
          name={from ? cityOf(from, locale) : ''}
        />
      </td>
      <td>
        <Named code={row.arrivalAirportIataCode} name={to ? cityOf(to, locale) : ''} />
      </td>
      {/*
        The status in words, with no pill around it — the row's own colour is the
        pill now, and a badge on top of it was a second background fighting the
        first: the badge's own `-soft` fill is near-white, so on a tinted row it
        read as a hole punched through it.

        The WORDS stay, and they are not decoration. The tint is colour-only, and
        it is shared — DEPARTED_LATE and LANDED_LATE are one amber — so this cell
        is both what a reader gets in greyscale and the only thing that separates
        two statuses that happen to look alike.
      */}
      <td className={styles.statusName}>
        {row.flightStatus ? t(`schedule.statuses.${row.flightStatus}`) : <Empty />}
      </td>
      {/*
        The date is stated ONCE per row, on the scheduled time, and only when the board
        covers more than one day. The other two columns then say how they differ from
        it — a day marker when they land on another date, and the minutes when they
        moved — which is what a reader actually wants from them. Three dates per row
        would be the same fact printed three times and the delay printed nowhere.
      */}
      <td className={styles.time}>
        <Clock value={scheduled} withDate={datedTimes} />
      </td>
      <td className={cx(styles.time, estimateMoved && styles.timeChanged)}>
        <Clock value={estimated} reference={scheduled} withDelta />
      </td>
      <td className={styles.time}>
        <Clock value={row.actualDate} reference={scheduled} withDelta />
      </td>

      <td className={styles.mono}>{row.planeParkingName ?? <Empty />}</td>

      {/*
        An IATA delay code, shown raw — translating a code nobody memorises into a
        guess would be worse than the code itself, which is the same call the flight
        card makes.

        The minutes go in the title rather than a column of their own: they are what
        makes the code mean something, and "93" beside nothing is a fact with no size.
      */}
      <td className={styles.mono}>
        {row.delayReason ? (
          <span
            title={
              row.delayMinutes !== null && row.delayMinutes !== 0
                ? t('schedule.detail.delayed', { count: row.delayMinutes })
                : undefined
            }
          >
            {row.delayReason}
          </span>
        ) : (
          <Empty />
        )}
      </td>

      <td className={styles.numeric}>
        {row.passengerAmount !== null ? formatNumber(row.passengerAmount) : <Empty />}
      </td>
    </tr>
  );
};

/**
 * A clock reading: the time, and whatever a reader needs beside it to place it.
 *
 * `withDate` adds the day and month, for a board that spans days. `withDelta` adds how
 * far the reading sits from the scheduled one, which is the entire question asked of an
 * estimate or an actual: `06:36` says little, `06:36 +54` says the flight is running
 * nearly an hour late.
 *
 * The day marker stays either way. `23:50` scheduled beside `00:10` estimated reads as
 * forty minutes EARLY unless the day is said, so a `+1` appears the way a timetable
 * writes it. The full timestamp is on hover throughout.
 */
const Clock = ({
  value,
  reference,
  withDate = false,
  withDelta = false,
}: {
  value: string | null;
  reference?: string | null;
  withDate?: boolean;
  withDelta?: boolean;
}) => {
  const { t, formatTime, formatDateTime } = useTranslation();

  if (!value) return <Empty />;

  const offset = reference ? dayOffset(reference, value) : 0;
  const drift = withDelta && reference ? minutesBetween(reference, value) : 0;

  return (
    <span title={formatDateTime(value)}>
      {withDate && <span className={styles.cellDate}>{dayMonth(value)}</span>}
      {formatTime(value)}
      {offset !== 0 && (
        <sup className={styles.dayOffset}>{offset > 0 ? `+${offset}` : offset}</sup>
      )}
      {drift !== 0 && (
        <span
          className={cx(styles.drift, drift > 0 ? styles.driftLate : styles.driftEarly)}
          title={t(drift > 0 ? 'schedule.laterBy' : 'schedule.earlierBy', {
            count: Math.abs(drift),
          })}
        >
          {formatDrift(drift)}
        </span>
      )}
    </span>
  );
};

/**
 * What the colours mean, from the Status header.
 *
 * Built by walking `FLIGHT_STATUSES` through the same `TINT` map the rows use, so
 * the key is generated from the board's own colouring rather than being a second
 * list to keep in step with it.
 */
/** Wide enough for two columns of status names; also what the clamp measures against. */
const LEGEND_WIDTH = 25;

const StatusLegend = () => {
  const { t } = useTranslation();
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);
  const toggleRef = useRef<HTMLSpanElement>(null);

  /**
   * Placed from a measured rect and portalled to the body, for the same reason the
   * column filters are: this lives in a header cell inside the table's scrollport,
   * and a scroll container clips on BOTH axes — so rendered in place it was cut off
   * by the very table it explains.
   *
   * The left edge is CLAMPED to the viewport. Status is the fifth of eleven
   * columns, so on a narrow window it is scrolled well to the right and a menu
   * pinned to the icon opened off the side of the screen — which is what made this
   * look broken rather than merely misplaced.
   */
  const place = () => {
    const rect = toggleRef.current?.getBoundingClientRect();
    if (!rect) return;

    const gutter = 8;
    const width = LEGEND_WIDTH * 16;
    setBox({
      top: rect.bottom + 4,
      left: Math.max(gutter, Math.min(rect.left, window.innerWidth - width - gutter)),
    });
  };

  // Derived so the effect below can depend on WHETHER it is open, not on where it
  // is — otherwise every reposition would tear down and rebuild these listeners.
  const isOpen = box !== null;

  useEffect(() => {
    if (!isOpen) return;

    // Followed rather than dismissed: the header it hangs off is sticky, so it
    // stays on screen while the rows move under it, and the key should stay with it.
    const reposition = () => {
      const rect = toggleRef.current?.getBoundingClientRect();
      if (!rect) return;

      const gutter = 8;
      const width = LEGEND_WIDTH * 16;
      setBox({
        top: rect.bottom + 4,
        left: Math.max(gutter, Math.min(rect.left, window.innerWidth - width - gutter)),
      });
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setBox(null);
    };

    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    window.addEventListener('resize', reposition);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('scroll', reposition, { capture: true });
      window.removeEventListener('resize', reposition);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  /*
    Hover opens it, which is what a key like this should cost: nothing but pointing
    at it. It is not a button any more either — there is nothing to press, and a
    control that only reveals text should not claim to do something.

    Focus and blur do the same thing, because hover is the one trigger a keyboard
    and a touch screen do not have. `tabIndex` is what makes that reachable, and
    `role="img"` with a label is how it announces itself: a graphic that means
    something, not an interactive element.
  */
  const open = () => place();
  const close = () => setBox(null);

  return (
    <span
      className={styles.legend}
      ref={toggleRef}
      tabIndex={0}
      role="img"
      aria-label={t('schedule.statusLegend')}
      title={t('schedule.statusLegend')}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      <InfoIcon className={cx(styles.legendIcon, isOpen && styles.legendIconOpen)} />

      {isOpen &&
        box !== null &&
        createPortal(
          <div
            className={styles.legendMenu}
            style={{ top: box.top, left: box.left, width: "auto" }}
          >
            <span className={styles.legendTitle}>{t('schedule.statusLegend')}</span>

            {FLIGHT_STATUSES.map((status) => (
              <span key={status} className={cx(styles.legendItem, TINT[status])}>
                <span className={styles.legendSwatch} aria-hidden="true" />
                {t(`schedule.statuses.${status}`)}
              </span>
            ))}
          </div>,
          document.body,
        )}
    </span>
  );
};

const Empty = () => <span className={styles.empty}>—</span>;
